import React, { useState, useEffect } from 'react';
import {
  Tabs,
  Tab,
  Table,
  Button,
  Container,
  Pagination,
  Spinner,
  Modal,
  Form,
  Row,
  Col
} from 'react-bootstrap';
import { BASEAPIURL, token } from '../../env';

// Helper to flatten sports bookings
const flattenSportsData = (data) => {
  const flattened = [];
  (data || []).forEach((booking) => {
    (booking.BookingDtl || []).forEach((detail) => {
      (detail.BookingMemDtl || []).forEach((member) => {
        flattened.push({
          BookingID: booking.BookingID,
          BookingNo: booking.BookingNo,
          BookingDate: booking.BookingDate,
          Facility: detail.Fty_Name,
          Court: detail.FtyCourt_Name,
          Slot: detail.Slot_Name,
          EventDate: detail.EventDate,
          MemberName: member.Name,
          MemberContact: member.MobileNo,
          MemberType: member.MemberType,
          MemberPrice: detail.MemberPrice,
          NetAmount: booking.BookingNetAmount,
          PaymentStatus: booking.BookingPaymentStatus,
        });
      });
    });
  });
  return flattened;
};

export default function BookingDashboard() {
  const [fullData, setFullData] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [activeKey, setActiveKey] = useState('Room');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [loading, setLoading] = useState(false);

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(''); // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState(null);

  // Form states
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchData('Room');
  }, []);

  const fetchData = async (type) => {
    setActiveKey(type);
    setCurrentPage(1);
    setLoading(true);

    try {
      if (type === 'Room') {
        const url = `${BASEAPIURL}/GetRoomBooking`; // Fixed URL
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokenData: token }),
        });
        const result = await response.json();
        const data = result?.data || [];
        setFullData(data);
        paginateData(data, 1);

      } else if (type === 'Lawn' || type === 'Banquet') {
        const url = `${BASEAPIURL}/GetLawnBooking?Token=${token}`;
        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        });

        const text = await response.text();
        const result = text ? JSON.parse(text) : {};
        const data = result?.data || [];
        setFullData(data);
        paginateData(data, 1);

      } else if (type === 'Sports') {
        const url = `${BASEAPIURL}/GetHealthBookingLists?Token=${token}`; // Fixed URL
        const response = await fetch(url, { method: 'GET' });

        const text = await response.text();
        const result = text ? JSON.parse(text) : [];
        const rawData = Array.isArray(result) ? result : [];
        const flattened = flattenSportsData(rawData);
        setFullData(flattened);
        paginateData(flattened, 1);

      } else {
        console.error(`Unknown type: ${type}`);
        setFullData([]);
        setBookingData([]);
      }
    } catch (error) {
      console.error(`${type} fetch failed`, error);
      setFullData([]);
      setBookingData([]);
    } finally {
      setLoading(false);
    }
  };

  const paginateData = (data, page) => {
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    setBookingData(data.slice(start, end));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    paginateData(fullData, page);
  };

  // Open modal for create or edit
  const openModal = (type, item = null) => {
    setModalType(type);
    setCurrentItem(item);

    if (type === 'edit' && item) {
      setFormData(item);
    } else {
      setFormData(getDefaultFormData());
    }
    setErrors({});
    setShowModal(true);
  };

  // Get default form data based on active tab
  const getDefaultFormData = () => {
    switch (activeKey) {
      case 'Room':
        return {
          RoomType: '',
          Member_Name: '',
          Member_Contact: '',
          TotalRoom: 1,
          CheckInDate: '',
          CheckOutDate: '',
          ExtraBed: 0
        };
      case 'Lawn':
      case 'Banquet':
        return {
          BookingNo: '',
          BookingDate: '',
          GuestName: '',
          GuestContact: '',
          MemberQuantity: 1
        };
      case 'Sports':
        return {
          BookingNo: '',
          EventDate: '',
          Facility: '',
          Court: '',
          Slot: '',
          MemberName: '',
          MemberContact: '',
          MemberType: '',
          NetAmount: 0
        };
      default:
        return {};
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    switch (activeKey) {
      case 'Room':
        if (!formData.RoomType) newErrors.RoomType = 'Room Type Name is required';
        if (!formData.Member_Name) newErrors.Member_Name = 'Member Name is required';
        if (!formData.Member_Contact) newErrors.Member_Contact = 'Member Contact is required';
        if (!formData.CheckInDate) newErrors.CheckInDate = 'Check-In Date is required';
        if (!formData.CheckOutDate) newErrors.CheckOutDate = 'Check-Out Date is required';
        break;
      case 'Lawn':
      case 'Banquet':
        if (!formData.BookingNo) newErrors.BookingNo = 'Booking Number is required';
        if (!formData.BookingDate) newErrors.BookingDate = 'Booking Date is required';
        if (!formData.GuestName) newErrors.GuestName = 'Guest Name is required';
        if (!formData.GuestContact) newErrors.GuestContact = 'Guest Contact is required';
        break;
      case 'Sports':
        if (!formData.BookingNo) newErrors.BookingNo = 'Booking Number is required';
        if (!formData.EventDate) newErrors.EventDate = 'Event Date is required';
        if (!formData.Facility) newErrors.Facility = 'Facility is required';
        if (!formData.Court) newErrors.Court = 'Court is required';
        if (!formData.Slot) newErrors.Slot = 'Slot is required';
        if (!formData.MemberName) newErrors.MemberName = 'Member Name is required';
        if (!formData.MemberContact) newErrors.MemberContact = 'Member Contact is required';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission (create/update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      let url, method, body;

      if (modalType === 'create') {
        // Determine the API endpoint based on active tab
        if (activeKey === 'Room') {
          url = `${BASEAPIURL}/CreateRoomBooking`;
          body = { ...formData, tokenData: token };
        } else if (activeKey === 'Lawn' || activeKey === 'Banquet') {
          url = `${BASEAPIURL}/CreateLawnBooking`;
          body = { ...formData, tokenData: token };
        } else if (activeKey === 'Sports') {
          url = `${BASEAPIURL}/CreateHealthBooking`;
          body = { ...formData, tokenData: token };
        }
        method = 'POST';
      } else {
        // For updates, we'll need specific endpoints for each booking type
        if (activeKey === 'Room') {
          url = `${BASEAPIURL}/UpdateRoomBooking`;
          body = { ...formData, BookingID: currentItem.BookingID, tokenData: token };
        } else if (activeKey === 'Lawn' || activeKey === 'Banquet') {
          url = `${BASEAPIURL}/UpdateLawnBooking`;
          body = { ...formData, BookingID: currentItem.BookingID, tokenData: token };
        } else if (activeKey === 'Sports') {
          url = `${BASEAPIURL}/UpdateHealthBooking`;
          body = { ...formData, BookingID: currentItem.BookingID, tokenData: token };
        }
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });

      const result = await response.json();

      if (response.ok && result.success) {
        alert(`Booking ${modalType === 'create' ? 'created' : 'updated'} successfully!`);
        setShowModal(false);
        fetchData(activeKey); // Refresh data
      } else {
        throw new Error(result.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Submit error:', error);
      alert(`Error: ${error.message || 'An error occurred during the operation'}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete
  const handleDelete = async (item) => {
    if (!window.confirm(`Are you sure you want to delete booking ID: ${item.BookingID}?`)) {
      return;
    }

    setLoading(true);

    try {
      let url;
      if (activeKey === 'Room') {
        url = `${BASEAPIURL}/DeleteRoomBooking?BookingID=${item.BookingID}&Token=${token}`;
      } else if (activeKey === 'Lawn' || activeKey === 'Banquet') {
        url = `${BASEAPIURL}/DeleteLawnBooking?BookingID=${item.BookingID}&Token=${token}`;
      } else if (activeKey === 'Sports') {
        url = `${BASEAPIURL}/DeleteHealthBooking?BookingID=${item.BookingID}&Token=${token}`;
      }

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        alert('Booking deleted successfully!');
        fetchData(activeKey); // Refresh data
      } else {
        throw new Error('Delete operation failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(`Error: ${error.message || 'An error occurred during deletion'}`);
    } finally {
      setLoading(false);
    }
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(fullData.length / pageSize);
    if (totalPages <= 1) return null;

    return (
      <div className="d-flex justify-content-center flex-wrap">
        <Pagination className="mt-3">
          <Pagination.First
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          />
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <Pagination.Item
              key={page}
              active={page === currentPage}
              onClick={() => handlePageChange(page)}
            >
              {page}
            </Pagination.Item>
          ))}
          <Pagination.Next
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          />
          <Pagination.Last
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
          />
        </Pagination>
      </div>
    );
  };

  const renderLawnTable = () => (
    <>
      <div className="table-scroll-wrapper">
        <Table bordered hover className="mt-3 text-nowrap">
          <thead className="table-dark">
            <tr>
              <th>Booking ID</th>
              <th>Booking No</th>
              <th>Booking Date</th>
              <th>Guest Name</th>
              <th>Guest Contact</th>
              <th>Member Quantity</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookingData.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center">No records found.</td>
              </tr>
            ) : (
              bookingData.map((item, index) => (
                <tr key={index}>
                  <td>{item.BookingID}</td>
                  <td>{item.BookingNo}</td>
                  <td>{new Date(item.BookingDate).toLocaleDateString()}</td>
                  <td>{item.GuestName}</td>
                  <td>{item.GuestContact}</td>
                  <td>{item.MemberQuantity}</td>
                  <td>
                    {/* <Button
                      variant="primary"
                      size="sm"
                      className="me-2"
                      onClick={() => openModal('edit', item)}
                    >
                      Edit
                    </Button> */}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-end mt-3">
        {/* <Button 
          variant="success" 
          onClick={() => openModal('create')}
        >
          Add New Booking
        </Button> */}
      </div>
      {renderPagination()}
    </>
  );

  const renderRoomTable = () => (
    <>
      <div className="table-scroll-wrapper">
        <Table bordered hover className="mt-3 text-nowrap">
          <thead className="table-dark">
            <tr>
              <th>Booking ID</th>
              <th>Room Type</th>
              <th>Member Name</th>
              <th>Member Contact</th>
              <th>Total Room</th>
              <th>Check-In</th>
              <th>Check-Out</th>
              <th>Extra Bed</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {bookingData.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center">No records found.</td>
              </tr>
            ) : (
              bookingData.map((item, index) => (
                <tr key={index}>
                  <td>{item.BookingID}</td>
                  <td>{item.RoomType || "--"}</td>
                  <td>{item.Member_Name}</td>
                  <td>{item.Member_Contact}</td>
                  <td>{item.TotalRoom}</td>
                  <td>{new Date(item.CheckInDate).toLocaleDateString()}</td>
                  <td>{new Date(item.CheckOutDate).toLocaleDateString()}</td>
                  <td>{item.ExtraBed}</td>
                  <td>
                    {/* <Button
                      variant="primary"
                      size="sm"
                      className="me-2"
                      onClick={() => openModal('edit', item)}
                    >
                      Edit
                    </Button> */}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDelete(item)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-end mt-3">
        {/* <Button 
          variant="success" 
          onClick={() => openModal('create')}
        >
          Add New Booking
        </Button> */}
      </div>
      {renderPagination()}
    </>
  );


const renderSportsTable = () => (
    <>
      <div className="table-scroll-wrapper">
        <Table bordered hover className="mt-3 text-nowrap">
          <thead className="table-dark">
            <tr>
              <th>Booking ID</th>
              <th>Booking No</th>
              <th>Booking Date</th>
              <th>Facility</th>
              <th>Court</th>
              <th>Slot</th>
              <th>Event Date</th>
              <th>Member Name</th>
              <th>Member Contact</th>
              <th>Member Type</th>
              <th>Member Price</th>
              <th>Net Amount</th>
              <th>Payment Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fullData.length === 0 ? (
              <tr>
                <td colSpan={14} className="text-center">No records found.</td>
              </tr>
            ) : (
              fullData.map((item, index) => (
                <tr key={`${item.BookingID}-${index}`}>
                  <td>{item.BookingID}</td>
                  <td>{item.BookingNo}</td>
                  <td>{new Date(item.BookingDate).toLocaleDateString()}</td>
                  <td>{item.Facility}</td>
                  <td>{item.Court}</td>
                  <td>{item.Slot}</td>
                  <td>{new Date(item.EventDate).toLocaleDateString()}</td>
                  <td>{item.MemberName}</td>
                  <td>{item.MemberContact}</td>
                  <td>{item.MemberType}</td>
                  <td>₹{item.MemberPrice?.toFixed(2)}</td>
                  <td>₹{item.NetAmount?.toFixed(2)}</td>
                  <td>{item.PaymentStatus}</td>
                  <td>
                    {/* <Button variant="primary" size="sm" className="me-2" >
                      Edit
                    </Button> */}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      {renderPagination()}
    </>
  );

  const renderModal = () => {
    if (!showModal) return null;

    return (
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {modalType === 'create' ? 'Create New Booking' : 'Edit Booking'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {activeKey === 'Room' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Room Type</Form.Label>
                    <Form.Control
                      type="text"
                      name="RoomType"
                      value={formData.RoomType || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.RoomType}
                      />
                    <Form.Control.Feedback type="invalid">
                      {errors.RoomType}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="Member_Name"
                      value={formData.Member_Name || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.Member_Name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Member_Name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Contact</Form.Label>
                    <Form.Control
                      type="text"
                      name="Member_Contact"
                      value={formData.Member_Contact || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.Member_Contact}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Member_Contact}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Total Rooms</Form.Label>
                    <Form.Control
                      type="number"
                      name="TotalRoom"
                      value={formData.TotalRoom || 1}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Check-In Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="CheckInDate"
                      value={formData.CheckInDate || ''}
                      onChange={handleInputChange}
                      min="1000-01-01"
                      max="9999-12-31"

                      isInvalid={!!errors.CheckInDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.CheckInDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Check-Out Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="CheckOutDate"
                      value={formData.CheckOutDate || ''}
                      onChange={handleInputChange}
                      min="1000-01-01"
                      max="9999-12-31"
                      isInvalid={!!errors.CheckOutDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.CheckOutDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Extra Beds</Form.Label>
                    <Form.Control
                      type="number"
                      name="ExtraBed"
                      value={formData.ExtraBed || 0}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {(activeKey === 'Lawn' || activeKey === 'Banquet') && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Booking Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="BookingNo"
                      value={formData.BookingNo || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.BookingNo}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.BookingNo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Booking Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="BookingDate"
                      value={formData.BookingDate || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.BookingDate}
                      min="1000-01-01"
                      max="9999-12-31"

                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.BookingDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Guest Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="GuestName"
                      value={formData.GuestName || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.GuestName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.GuestName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Guest Contact</Form.Label>
                    <Form.Control
                      type="text"
                      name="GuestContact"
                      value={formData.GuestContact || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.GuestContact}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.GuestContact}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      name="MemberQuantity"
                      value={formData.MemberQuantity || 1}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}

            {activeKey === 'Sports' && (
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Booking Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="BookingNo"
                      value={formData.BookingNo || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.BookingNo}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.BookingNo}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Event Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="EventDate"
                      value={formData.EventDate || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.EventDate}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.EventDate}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Facility</Form.Label>
                    <Form.Control
                      type="text"
                      name="Facility"
                      value={formData.Facility || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.Facility}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Facility}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Court</Form.Label>
                    <Form.Control
                      type="text"
                      name="Court"
                      value={formData.Court || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.Court}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Court}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Slot</Form.Label>
                    <Form.Control
                      type="text"
                      name="Slot"
                      value={formData.Slot || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.Slot}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.Slot}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="MemberName"
                      value={formData.MemberName || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.MemberName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.MemberName}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Contact</Form.Label>
                    <Form.Control
                      type="text"
                      name="MemberContact"
                      value={formData.MemberContact || ''}
                      onChange={handleInputChange}
                      isInvalid={!!errors.MemberContact}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.MemberContact}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Member Type</Form.Label>
                    <Form.Control
                      type="text"
                      name="MemberType"
                      value={formData.MemberType || ''}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Net Amount</Form.Label>
                    <Form.Control
                      type="number"
                      name="NetAmount"
                      value={formData.NetAmount || 0}
                      onChange={handleInputChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    className="me-2"
                  />
                  {modalType === 'create' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                modalType === 'create' ? 'Create' : 'Update'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    );
  };

  return (
    <Container fluid className="p-3 p-md-4">
      <h2 className="mb-4 text-center text-md-start">Booking Dashboard</h2>

      <Tabs
        activeKey={activeKey}
        onSelect={(k) => fetchData(k)}
        id="booking-tabs"
        className="mb-3"
        justify
        fill
      >
        <Tab eventKey="Room" title="Room" />
        <Tab eventKey="Lawn" title="Lawn" />
        <Tab eventKey="Banquet" title="Banquet Hall" />
        <Tab eventKey="Sports" title="Sports" />
      </Tabs>

      {loading && !showModal ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {activeKey === 'Room' && renderRoomTable()}
          {(activeKey === 'Lawn' || activeKey === 'Banquet') && renderLawnTable()}
          {activeKey === 'Sports' && renderSportsTable()}
        </>
      )}

      {renderModal()}
    </Container>
  );
}